// src/app/home/home.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { SettingsOffcanvasComponent } from '../settings-offcanvas/settings-offcanvas.component';
import { Subscription } from 'rxjs';

import { AuthService } from '../core/services/auth.service';
import { SchoolService } from '../core/services/school.service';
import {
  School,
  GetTermApiResponse,
  SchoolDetailsResponse,
  ApplicationData 
} from '../core/models/auth.model';
import { environment } from '../../environments/environment';

export interface ApiTermData {
  id: number;
  name_en: string;
  name_zh_hk: string;
  name_zh_cn: string;
  start_date: string;
  end_date: string;
  tnc_file?: string | null;
}

interface SchoolInfoDisplay {
  name: string;
  assignedDates: string;
  assignedTimeslot: string;
  ridingSchool: string;
  assignedWeekday: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SettingsOffcanvasComponent
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit, OnDestroy {

  schoolInfo: SchoolInfoDisplay = {
    name: '',
    assignedDates: '',
    assignedTimeslot: '',
    ridingSchool: '',
    assignedWeekday: ''
  };

  isLoadingSchoolInfo = false;
  schoolDetailsError: string | null = null;
  isLoadingTermInfo = false;
  termInfoError: string | null = null;

  private rawApiTermData: ApiTermData | null = null;
  termTncFileFullUrl: string | null = null;
  private langChangeSubscription: Subscription | undefined;

  applications: ApplicationData[] = [];
  isLoadingApplications = false;
  applicationsError: string | null = null;
  private applicationsSubscription: Subscription | undefined;

  schoolConsentChecked: boolean = false;
  showConsentError: boolean = false;

  private readonly weekdayNames: { [key: number]: { en: string; 'zh-CN': string; 'zh-HK': string } } = {
    1: { en: 'Monday', 'zh-CN': '星期一', 'zh-HK': '星期一' },
    2: { en: 'Tuesday', 'zh-CN': '星期二', 'zh-HK': '星期二' },
    3: { en: 'Wednesday', 'zh-CN': '星期三', 'zh-HK': '星期三' },
    4: { en: 'Thursday', 'zh-CN': '星期四', 'zh-HK': '星期四' },
    5: { en: 'Friday', 'zh-CN': '星期五', 'zh-HK': '星期五' },
    6: { en: 'Saturday', 'zh-CN': '星期六', 'zh-HK': '星期六' },
    7: { en: 'Sunday', 'zh-CN': '星期日', 'zh-HK': '星期日' },
  };

  constructor(
    private router: Router,
    public translate: TranslateService,
    private schoolService: SchoolService,
    private authService: AuthService
  ) {
    // this.translate.setDefaultLang('en');
    // this.translate.use(this.translate.getBrowserLang() || 'en');
  }

  ngOnInit(): void {
    console.log('Home component initialized');
    this.initializePlaceholders();

    this.loadSchoolInfo();
    this.loadTermData(); 
    this.loadApplicationsData(); 

    this.langChangeSubscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      console.log('Language changed in Home component to:', event.lang);
      this.updateDisplayedTermName(); 
      this.updateDisplayedWeekday(); 
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
    if (this.applicationsSubscription) {
      this.applicationsSubscription.unsubscribe();
    }
  }

  initializePlaceholders(): void {
    this.schoolInfo.name = this.translate.instant('HOME.LOADING_SCHOOL_INFO_GENERIC');
    this.schoolInfo.ridingSchool = this.translate.instant('HOME.LOADING_SCHOOL_INFO_GENERIC');
    this.schoolInfo.assignedDates = this.translate.instant('HOME.LOADING_DATES_INFO');
    this.schoolInfo.assignedTimeslot = this.translate.instant('HOME.LOADING_TIMESLOT_INFO');
    this.schoolInfo.assignedWeekday = this.translate.instant('HOME.LOADING_WEEKDAY_INFO');
    this.termTncFileFullUrl = null;
  }

  loadSchoolInfo(): void {
    this.isLoadingSchoolInfo = true;
    this.schoolDetailsError = null;

    this.schoolService.getAuthenticatedSchoolDetails().subscribe({
      next: (response: SchoolDetailsResponse) => {
        this.isLoadingSchoolInfo = false;
        if (response && response.status === 'ok' && response.school) {
          const apiSchoolData = response.school;
          this.schoolInfo.name = apiSchoolData.name;
        } else {
          this.schoolInfo.name = this.translate.instant('HOME.ERROR_PLACEHOLDER_NAME');
          this.schoolInfo.ridingSchool = this.translate.instant('HOME.ERROR_PLACEHOLDER_ADDRESS');
          this.schoolDetailsError = this.extractErrorMessage(response) || this.translate.instant('HOME.ERROR_LOADING_SCHOOL_INFO');
        }
      },
      error: (err) => {
        this.isLoadingSchoolInfo = false;
        this.schoolInfo.name = this.translate.instant('HOME.ERROR_PLACEHOLDER_NAME');
        this.schoolInfo.ridingSchool = this.translate.instant('HOME.ERROR_PLACEHOLDER_ADDRESS');
        this.schoolDetailsError = this.translate.instant('HOME.ERROR_FETCHING_SCHOOL_INFO');
        console.error('Failed to fetch school details:', err);
      }
    });
  }

  loadTermData(): void {
    this.isLoadingTermInfo = true;
    this.termInfoError = null;
    this.termTncFileFullUrl = null;

    this.schoolService.getTerms().subscribe({
      next: (response: GetTermApiResponse) => {
        this.isLoadingTermInfo = false;
        if (response && response.status === 'ok' && response.term) {
          this.rawApiTermData = response.term;

          if (this.rawApiTermData.tnc_file) {
            let relativePath = this.rawApiTermData.tnc_file;
            if (relativePath.startsWith('/')) {
              relativePath = relativePath.substring(1);
            }
            const baseFileUrl = environment.apiUrl.endsWith('/api') ?
                                environment.apiUrl.substring(0, environment.apiUrl.length - '/api'.length) :
                                environment.apiUrl;

            this.termTncFileFullUrl = `${baseFileUrl}/storage/${relativePath}`;
            console.log('Constructed T&C File URL:', this.termTncFileFullUrl);
          }
          this.updateDisplayedTermName(); 

        } else {
          this.schoolInfo.assignedDates = 'N/A'; 
          this.schoolInfo.assignedTimeslot = 'N/A';
          this.schoolInfo.assignedWeekday = 'N/A';
          this.termInfoError = this.extractErrorMessage(response) || this.translate.instant('HOME.ERROR_LOADING_TERM_INFO');
        }
      },
      error: (err) => {
        this.isLoadingTermInfo = false;
        this.schoolInfo.assignedDates = 'N/A';
        this.schoolInfo.assignedTimeslot = 'N/A';
        this.schoolInfo.assignedWeekday = 'N/A';
        this.termInfoError = this.translate.instant('HOME.ERROR_FETCHING_TERM_INFO');
        console.error('Failed to fetch term info:', err);
      }
    });
  }

  loadApplicationsData(): void {
    this.isLoadingApplications = true;
    this.applicationsError = null;

    this.applicationsSubscription = this.authService.activeApplications$.subscribe({
      next: (apps: ApplicationData[] | null) => {
        this.isLoadingApplications = false;
        if (apps && apps.length > 0) {
          this.applications = apps;
          console.log('HomeComponent has received applications from AuthService:', this.applications);

          const firstApp = apps[0];

          this.schoolInfo.ridingSchool = firstApp.venue_name || this.translate.instant('HOME.VENUE_NAME_NOT_AVAILABLE');
          if (firstApp.timeslot_start_time && firstApp.timeslot_end_time) {
            this.schoolInfo.assignedDates = `${firstApp.timeslot_start_time.substring(0, 5)} - ${firstApp.timeslot_end_time.substring(0, 5)}`;
          } else {
            this.schoolInfo.assignedDates = 'N/A';
          }
          if (firstApp.timeslot_weekday !== null && firstApp.timeslot_weekday !== undefined) {
              this.schoolInfo.assignedWeekday = this.getLocalizedWeekday(firstApp.timeslot_weekday);
          } else {
              this.schoolInfo.assignedWeekday = 'N/A';
          }

          this.updateDisplayedTermName();

        } else {
          this.applications = [];
          this.applicationsError = this.translate.instant('HOME.NO_APPLICATIONS_FOUND');
          console.log('HomeComponent: No active term application data found.');
          this.schoolInfo.ridingSchool = 'N/A';
          this.schoolInfo.assignedDates = 'N/A';
          this.schoolInfo.assignedTimeslot = 'N/A';
          this.schoolInfo.assignedWeekday = 'N/A';
        }
      },
      error: (err) => {
        this.isLoadingApplications = false;
        this.applications = [];
        this.applicationsError = this.translate.instant('HOME.ERROR_LOADING_APPLICATIONS_FROM_SERVICE');
        console.error('HomeComponent: Error fetching applications from AuthService:', err);
        this.schoolInfo.ridingSchool = 'N/A';
        this.schoolInfo.assignedDates = 'N/A';
        this.schoolInfo.assignedTimeslot = 'N/A';
        this.schoolInfo.assignedWeekday = 'N/A';
      }
    });
  }
  public getLocalizedWeekday(dayNumber: number): string {
    const currentLang = this.translate.currentLang as 'en' | 'zh-CN' | 'zh-HK'; 
    const weekdayData = this.weekdayNames[dayNumber];

    if (weekdayData) {
      switch (currentLang) {
        case 'zh-CN':
          return weekdayData['zh-CN'];
        case 'zh-HK':
          return weekdayData['zh-HK'];
        case 'en':
        default:
          return weekdayData.en;
      }
    }
    return 'N/A'; 
  }
  private updateDisplayedTermName(): void {
    if (this.rawApiTermData) {
      const currentLang = this.translate.currentLang;
      if (currentLang === 'zh-CN' && this.rawApiTermData.name_zh_cn) {
        this.schoolInfo.assignedTimeslot = this.rawApiTermData.name_zh_cn;
      } else if (currentLang === 'zh-HK' && this.rawApiTermData.name_zh_hk) {
        this.schoolInfo.assignedTimeslot = this.rawApiTermData.name_zh_hk;
      } else if (this.rawApiTermData.name_en) {
        this.schoolInfo.assignedTimeslot = this.rawApiTermData.name_en;
      } else {
        this.schoolInfo.assignedTimeslot = 'N/A';
      }
    } else {
        if (!this.isLoadingTermInfo) { 
            this.schoolInfo.assignedTimeslot = 'N/A';
        }
    }
  }
  private updateDisplayedWeekday(): void {
      if (this.applications && this.applications.length > 0) {
          const firstApp = this.applications[0];
          if (firstApp.timeslot_weekday !== null && firstApp.timeslot_weekday !== undefined) {
              this.schoolInfo.assignedWeekday = this.getLocalizedWeekday(firstApp.timeslot_weekday);
          } else {
              this.schoolInfo.assignedWeekday = 'N/A';
          }
      } else if (!this.isLoadingApplications && !this.isLoadingTermInfo) {
          this.schoolInfo.assignedWeekday = 'N/A';
      }
  }


  private extractErrorMessage(response: any): string | null {
    if (!response) return null;
    if (response.message) return response.message;
    if (response.error && typeof response.error === 'string') return response.error;
    if (response.errors) {
      const errorMessagesArray: string[] = [];
      Object.keys(response.errors).forEach(key => {
        const messages = response.errors[key];
        if (messages && Array.isArray(messages)) { errorMessagesArray.push(...messages); }
        else if (typeof messages === 'string') { errorMessagesArray.push(messages); }
      });
      if (errorMessagesArray.length > 0) { return errorMessagesArray.join(' '); }
    }
    return null;
  }
  downloadTncFile(): void {
    if (this.termTncFileFullUrl) {
      window.open(this.termTncFileFullUrl, '_blank');
      console.log('Attempting to open T&C file:', this.termTncFileFullUrl);
    } else {
      console.warn('T&C file URL is not available to download.');
    }
  }

  consentChanged(): void {
    if (this.schoolConsentChecked) {
      this.showConsentError = false;
    }
  }

  handleConsentCheckOnClick(): void {
    if (!this.schoolConsentChecked) {
      this.showConsentError = true;
    }
  }

  onAddNewStudentProfile(): void {
    if (!this.schoolConsentChecked) {
      this.showConsentError = true;
      return;
    }
    this.showConsentError = false;
    this.router.navigate(['/add-student-profile']);
  }
}
